<template>
  <a-board :over="over" />
</template>

<script setup>
import {fabric} from 'fabric'
import {onMounted, onUnmounted, watch} from 'vue'
import {useTransformStore, useSnapshotStore, useSessionStore} from '@/stores'
import {useMeetingStore} from '@/stores'
import {Dart} from '@/libraries/fabric'
import {board as messages} from '@/messages'
import {capitalizeFirst} from '@/utils'
import {useBoard} from '@/procedures/useBoard'
import {useSnapshot} from '@/composables/useSnapshot'
import {useEventBus, useWindowSize} from '@vueuse/core'
const {subscribe, save, unsubscribe} = useSnapshot()

defineProps({over: Boolean})

const {width: windowWidth, height: windowHeight} = useWindowSize()
const snapshot = useSnapshotStore()
const session = useSessionStore()
const transform = useTransformStore()
const meetingStore = useMeetingStore()
const boardProcedure = useBoard()
onMounted(() => {
  subscribe('snapshot-created', snapshotListener)
})
onUnmounted(() => {
  unsubscribe('snapshot-created', snapshotListener)
})

const dart = new Dart()
let canvas = null

const state = {
  listening: false,
}

// STATE HANDLERS

watch(
  () => meetingStore.path,
  path => {
    if (!canvas) return
    switch (path) {
      case 'call': {
        canvas.clear()
        snapshot.setPlaceholder(false)
        transform.setTransform({
          zoom: 1,
          x: 0,
          y: 0,
        })
        break
      }
      case 'draw.default': {
        canvas.backgroundColor = 'black'
        break
      }
    }
  },
  {flush: 'sync'}
)

// STORE HANDLERS

transform.$onAction(({name, args, after}) => {
  let viewportTransform = [1, 0, 0, 1, 0, 0]
  switch (name) {
    case 'setTransform': {
      const [{zoom, x, y}] = args
      viewportTransform = [zoom, 0, 0, zoom, x, y]
      dart.updateScale(zoom)
      break
    }
    case 'setTranslation': {
      const [{x, y}] = args
      const {zoom} = transform
      viewportTransform = [zoom, 0, 0, zoom, x, y]
      break
    }
  }
  after(() => {
    canvas.viewportTransform = viewportTransform
    canvas.renderAll()
  })
})
watch([windowWidth, windowHeight], data => {
  const [width, height] = data
  canvas.setDimensions({
    width,
    height,
  })
})

// CANVAS HANDLERS

function setCanvasEventHandlers() {
  canvas
    .on('mouse:down', event => {
      const {x: left, y: top} = canvas.getPointer(event.e)
      state.listening = true
      boardProcedure.send(
        messages.tapCastedMessage({
          action: 'start',
          left,
          top,
        })
      )
    })
    .on('mouse:move', event => {
      if (state.listening) {
        const {x: left, y: top} = canvas.getPointer(event.e)
        boardProcedure.send(
          messages.tapCastedMessage({
            action: 'move',
            left,
            top,
          })
        )
      }
    })
    .on('mouse:up', () => {
      if (state.listening) {
        state.listening = false
        boardProcedure.send(
          messages.tapCastedMessage({
            action: 'stop',
          })
        )
      }
    })
}

// PROCEDURE LISTENERS

useEventBus('board:shape:created').on(data => {
  const {type} = data
  let object = {}
  if (type === 'path') {
    fabric.Path.fromObject(data, path => {
      object = path
    })
  } else {
    object = new fabric[capitalizeFirst(type)](type === 'i-text' ? '' : null)
    object.set(data)
  }
  canvas.add(object).renderAll()
})
useEventBus('board:shape:modified').on(data => {
  const {id} = data
  const object = canvas.getObjectById(id)
  object.set(data).setCoords()
  canvas.renderAll()
})
useEventBus('board:shape:removed').on(data => {
  const {id} = data
  const object = canvas.getObjectById(id)
  canvas.remove(object).renderAll()
})
useEventBus('board:dart:casted').on(data => {
  const {action} = data
  if (action === 'stop') {
    canvas.remove(dart.shape)
  } else {
    dart.shape.set(data)
    if (action === 'start') {
      canvas.add(dart.shape)
    }
  }
  canvas.renderAll()
})

function snapshotListener(event) {
  const {isThumbnail, image} = event.detail
  return addSnapshot(image, isThumbnail)
}

// METHODS
function addSnapshot(data, isThumbnail) {
  return new Promise((resolve, reject) => {
    new fabric.Image().setSrc(data, image => {
      if (meetingStore.isDraw) {
        // A placeholder that arrives after the full image must not replace it
        if (isThumbnail && canvas.getObjects().some(obj => obj.type === 'image' && !obj.isThumbnail)) {
          resolve()
          return
        }
        image.initSnapshot({width: windowWidth.value, height: windowHeight.value})
        image.isThumbnail = isThumbnail
        const {scaleX: scale, left, top, width, height} = image
        snapshot.setTransform({scale, left, top, width, height})
        const existingSnapshot = canvas.getObjects().find(obj => {
          return obj.type === 'image' && obj.isThumbnail
        })
        if (existingSnapshot) {
          canvas.remove(existingSnapshot)
        }
        canvas.add(image).sendToBack(image).renderAll()
        snapshot.setPlaceholder(!!isThumbnail)
        resolve()
      } else {
        reject()
      }
    })
  })
}
// LIFECYCLE HOOKS

onMounted(() => {
  canvas = new fabric.Canvas('board', {
    width: windowWidth.value,
    height: windowHeight.value,
  })
  canvas.renderAll()
  setCanvasEventHandlers()
})
useEventBus('board:signal:snapshot:save').on(() => saveSnapshot())

function saveSnapshot() {
  const {width: snapshotWidth, height: snapshotHeight} = snapshot.transform
  const {
    top,
    left,
    scale,
    client: {width: clientWidth, height: clientHeight},
  } = session.transform
  const userWidth = windowWidth.value
  const userHeight = windowHeight.value
  const scaledWidth = clientWidth * scale
  const scaledHeight = clientHeight * scale
  const multiplier = 1 / Math.min(userWidth / snapshotWidth, userHeight / snapshotHeight, 1)
  const data = canvas.toDataURL({
    format: 'jpeg',
    quality: 0.92,
    left,
    top,
    width: scaledWidth,
    height: scaledHeight,
    multiplier,
  })
  save(data)
}
</script>
